import { signOut } from '../../auth/staffAuthApi';
import {
  clearPendingSecondFactor,
  clearSecondFactorVerifiedSession,
} from '../../features/two-factor-auth/utils/pendingSecondFactorSession';

export const WEBSITE_LOGIN_ATTEMPT_KEY = 'websiteLoginAttemptInProgress';

const WEBSITE_OWNER_SESSION_KEY = 'website_owner_session';
export const WEBSITE_OWNER_UID_STORAGE_KEY = 'website_owner_session_uid';
const AUTH_SYNC_CHANNEL = 'neatschedule-auth-sync';
export const APP_SECOND_FACTOR_IN_PROGRESS_KEY = 'website_app_second_factor_in_progress';

const WEBSITE_LOCAL_STORAGE_PRESERVE_KEYS = new Set([
  WEBSITE_OWNER_UID_STORAGE_KEY,
  'websiteAuthUser',
  'websiteRememberMe',
]);

export const WEBSITE_SIGNUP_PATHS = ['/main/account-setup', '/main/verify-email'];

const APP_ONLY_ROLES = new Set(['employee', 'manager']);

export function isWebsiteSignupPath(pathname = window.location.pathname) {
  return WEBSITE_SIGNUP_PATHS.some((path) => pathname.startsWith(path));
}

export function isWebsiteOwnerLoginPath(pathname = window.location.pathname) {
  if (isWebsiteSignupPath(pathname)) return false;
  return pathname.startsWith('/main/login');
}

export function isAppRoute(pathname = window.location.pathname) {
  return pathname.startsWith('/app');
}

export function canUseWebsiteOwnerPortalAuth(userRole, uid, pathname = window.location.pathname) {
  return userRole === 'owner' && isWebsiteOwnerSession(uid) && !isAppRoute(pathname);
}

export function isWebsiteLoginAttemptInProgress() {
  try {
    return sessionStorage.getItem(WEBSITE_LOGIN_ATTEMPT_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setWebsiteLoginAttemptInProgress(active) {
  try {
    if (active) {
      sessionStorage.setItem(WEBSITE_LOGIN_ATTEMPT_KEY, 'true');
    } else {
      sessionStorage.removeItem(WEBSITE_LOGIN_ATTEMPT_KEY);
    }
  } catch {
    // ignore storage failures
  }
}

/** Website owner portal access (billing/account) — separate from verified app 2FA. */
export function markWebsiteOwnerSession(uid) {
  try {
    sessionStorage.setItem(WEBSITE_OWNER_SESSION_KEY, '1');
    if (uid) {
      localStorage.setItem(WEBSITE_OWNER_UID_STORAGE_KEY, String(uid));
    }
  } catch {
    // ignore storage failures
  }
}

/** Re-assert website portal access in all tabs after app 2FA completes. */
export function notifyWebsitePortalSessionSync(uid) {
  if (!uid) return;
  markWebsiteOwnerSession(uid);
  clearAppSecondFactorInProgress();
  try {
    const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
    channel.postMessage({ type: 'website-portal-sync', uid: String(uid) });
    channel.close();
  } catch {
    // ignore unsupported environments
  }
}

export function subscribeWebsitePortalSessionSync(onSync) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let channel;
  try {
    channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
    channel.onmessage = (event) => {
      if (event?.data?.type === 'website-portal-sync' && event.data.uid) {
        onSync(String(event.data.uid));
      }
    };
  } catch {
    channel = null;
  }

  const onStorage = (event) => {
    if (event.key === 'app_second_factor_verified_uid' && event.newValue) {
      onSync(String(event.newValue));
    }
  };

  window.addEventListener('storage', onStorage);

  return () => {
    if (channel) {
      channel.close();
    }
    window.removeEventListener('storage', onStorage);
  };
}

export function shouldPreserveWebsiteLocalStorageKey(key) {
  return WEBSITE_LOCAL_STORAGE_PRESERVE_KEYS.has(key) || String(key).startsWith('website_');
}

export function hasWebsiteOwnerPortalMarker(uid) {
  try {
    if (!uid) {
      return false;
    }
    return isWebsiteOwnerSession(uid);
  } catch {
    return false;
  }
}

export function markAppSecondFactorInProgress(uid) {
  if (!uid) return;
  try {
    sessionStorage.setItem(APP_SECOND_FACTOR_IN_PROGRESS_KEY, String(uid));
  } catch {
    // ignore storage failures
  }
}

export function clearAppSecondFactorInProgress() {
  try {
    sessionStorage.removeItem(APP_SECOND_FACTOR_IN_PROGRESS_KEY);
  } catch {
    // ignore storage failures
  }
}

export function isAppSecondFactorInProgress(uid) {
  try {
    const stored = sessionStorage.getItem(APP_SECOND_FACTOR_IN_PROGRESS_KEY);
    if (!stored || !uid) return false;
    return stored === String(uid);
  } catch {
    return false;
  }
}

/** Remove stale cross-tab auth markers when there is no active session. */
export function clearStaleWebsitePortalMarkers() {
  clearWebsitePortalAuthStorage();
}

/** Full website owner portal sign-out cleanup (local markers + cached auth payload). */
export function clearWebsitePortalAuthStorage() {
  clearWebsiteOwnerSession();
  try {
    localStorage.removeItem('websiteAuthUser');
    localStorage.removeItem('websiteRememberMe');
  } catch {
    // ignore storage failures
  }
}

/** Website portal access must survive while the app tab sends/completes its own 2FA. */
export function shouldPreserveWebsitePortalDuringAppAuth(uid, pathname = window.location.pathname) {
  if (isAppRoute(pathname)) return false;
  return hasWebsiteOwnerPortalMarker(uid) || isAppSecondFactorInProgress(uid);
}

/** Tell other tabs the app is starting 2FA so the website portal stays signed in. */
export function notifyAppSecondFactorStarted(uid) {
  if (!uid) return;
  markWebsiteOwnerSession(uid);
  markAppSecondFactorInProgress(uid);
  try {
    const channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
    channel.postMessage({ type: 'app-second-factor-started', uid: String(uid) });
    channel.close();
  } catch {
    // ignore unsupported environments
  }
}

export function subscribeAppSecondFactorLifecycle({ onStarted, onCompleted } = {}) {
  if (typeof window === 'undefined') {
    return () => {};
  }

  let channel;
  try {
    channel = new BroadcastChannel(AUTH_SYNC_CHANNEL);
    channel.onmessage = (event) => {
      const { type, uid } = event?.data || {};
      if (type === 'app-second-factor-started' && uid && onStarted) {
        onStarted(String(uid));
      }
      if (type === 'website-portal-sync' && uid && onCompleted) {
        onCompleted(String(uid));
      }
    };
  } catch {
    channel = null;
  }

  return () => {
    if (channel) {
      channel.close();
    }
  };
}

export function isWebsiteOwnerSession(uid) {
  try {
    const storedUid = localStorage.getItem(WEBSITE_OWNER_UID_STORAGE_KEY);
    if (uid && storedUid && storedUid !== String(uid)) {
      return false;
    }
    if (sessionStorage.getItem(WEBSITE_OWNER_SESSION_KEY) === '1') {
      return Boolean(uid) && (!storedUid || storedUid === String(uid));
    }
    if (!uid) {
      return false;
    }
    return storedUid === String(uid);
  } catch {
    return false;
  }
}

export function clearWebsiteOwnerSession() {
  try {
    sessionStorage.removeItem(WEBSITE_OWNER_SESSION_KEY);
    sessionStorage.removeItem(APP_SECOND_FACTOR_IN_PROGRESS_KEY);
    localStorage.removeItem(WEBSITE_OWNER_UID_STORAGE_KEY);
  } catch {
    // ignore storage failures
  }
}

export function shouldRejectNonOwnerWebsiteSession(userData, pathname = window.location.pathname) {
  if (!userData?.role || userData.role === 'owner' || userData.isSpecialAdmin) {
    return false;
  }
  if (!APP_ONLY_ROLES.has(userData.role)) {
    return false;
  }
  if (isWebsiteSignupPath(pathname)) {
    return false;
  }
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('accountSetupInProgress') === 'true') {
    return false;
  }
  return isWebsiteOwnerLoginPath(pathname) || isWebsiteLoginAttemptInProgress();
}

export async function clearRejectedWebsiteAppSession(auth) {
  clearSecondFactorVerifiedSession();
  clearPendingSecondFactor();
  clearWebsiteOwnerSession();
  try {
    localStorage.removeItem('websiteAuthUser');
    localStorage.removeItem('websiteRememberMe');
  } catch {
    // ignore storage failures
  }
  try {
    if (auth?.currentUser) {
      await signOut(auth);
    }
  } catch {
    // ignore sign-out failures
  }
}
